import { inject, Injectable, Injector } from '@angular/core'
import {
  CreateNewDraft,
  DraftStatus,
  MarketLayerMapping,
  RecentDraft,
} from '../models/draft-selection.model'
import { SubmitToMarketAPIService } from 'src/app/api/submit-to-market.ts/submit-to-market.services'
import { rejectError } from 'src/app/api/util'
import { MatSnackBar } from '@angular/material/snack-bar'
import { ErrorComponent } from 'src/app/error/error.component'
import { errorPayload } from 'src/app/error/model/error'
import { Router } from '@angular/router'
import { SUBMIT_TO_MARKET_PATHS } from '../models/routing.model'
import { SubmitToMarketLocalActions } from '../store/submit-to-market.actions'
import { Store } from '@ngrx/store'
import { AppState } from 'src/app/core/store'
import { LoadingBlock, LoadingTasks } from '../models/loading.model'
import { finalize, firstValueFrom, map, tap } from 'rxjs'
import { UploadAttachment } from '../models/attachments.model'
import {
  AttachmentMapping,
  DraftDashboardListItem,
  EmailTemplateMapping,
  EmailTemplate,
  Contact,
} from '../models/draft-data.model'
import { EmailDto } from '../models/send-model'
import { RecentDraftsService } from './recent-structures.service'
import { STMMarketOverride } from '../models/draft.model'
import { SageLinkService } from './sage-link.service'
import {
  SageLinkAdeptMarketSubmissionResponse,
  STMSageLinkDisplayResponse,
} from '../models/submit-to-market-sage-link.model'
import { fetchLatestPrograms } from 'src/app/analysis/store/analysis.actions'
import { InteractionTrackingService } from 'src/app/core/telemetry/interaction-tracking.service'

@Injectable({
  providedIn: 'root',
})
export class DraftActionsService {
  submitToMarketApiService = inject(SubmitToMarketAPIService)
  sageLinkService = inject(SageLinkService)
  router = inject(Router)
  snackbar = inject(MatSnackBar)
  store = inject(Store<AppState>)
  interactions = inject(InteractionTrackingService)

  private createLoadingLock(task: LoadingTasks): number {
    const block: LoadingBlock = {
      id: Date.now() * Math.random(),
      task,
    }
    this.store.dispatch(SubmitToMarketLocalActions.addLoadingBlock(block))
    return block.id
  }

  private errorAndExit(error: string) {
    this.snackbar.openFromComponent(ErrorComponent, {
      data: errorPayload(error),
      panelClass: 'app-error',
      duration: 3000,
    })
    this.router.navigate(['submit-to-market'])
  }

  public exit(): void {
    this.router.navigate(['submit-to-market'])
  }

  private clearLoadingBlock(id: number) {
    this.store.dispatch(SubmitToMarketLocalActions.clearLoadingBlock({ id }))
  }

  public async createNewDraft(draft: CreateNewDraft) {
    const loadingBlockId = this.createLoadingLock(LoadingTasks.CreatingNewDraft)
    const draftItem = await firstValueFrom(
      this.submitToMarketApiService.createNewDraft(draft).pipe(
        rejectError((error) => {
          this.errorAndExit(`Failed to create a new draft`)
        }),
      ),
    )
    this.router.navigate([
      'submit-to-market',
      SUBMIT_TO_MARKET_PATHS.Draft,
      draftItem.id,
      SUBMIT_TO_MARKET_PATHS.SelectStructures,
    ])
    this.clearLoadingBlock(loadingBlockId)

    return draftItem
  }

  public deleteDraft(draftId: number): Promise<void> {
    return firstValueFrom(
      this.submitToMarketApiService.deleteDraft(draftId).pipe(
        rejectError((error) => {
          this.errorAndExit(`Failed to delete placement`)
        }),
      ),
    )
  }

  public getRecentDrafts(drafts: RecentDraft[]) {
    return this.submitToMarketApiService.getRecentDraftStatus(drafts).pipe(
      rejectError((error) => {
        console.warn(`Failed to update drafts: ${error}`)
      }),
    )
  }

  public getDraftData(draftId: number): void {
    const loadingBlockId = this.createLoadingLock(LoadingTasks.FetchingDraftData)
    this.submitToMarketApiService
      .getDraftData(draftId)
      .pipe(
        rejectError((error) => {
          this.errorAndExit(`Failed to get draft structures`)
        }),
      )
      .subscribe((data) => {
        ;(this.store.dispatch(SubmitToMarketLocalActions.draftDataFetchSuccess(data)),
          this.clearLoadingBlock(loadingBlockId))
      })
  }

  public getSTMDataToDisplay(draftId: number, queryParams?: URLSearchParams) {
    const loadingBlockId = this.createLoadingLock(LoadingTasks.FetchingSTMData)
    return this.submitToMarketApiService
      .getSTMDataToDisplay(draftId, queryParams?.toString())
      .pipe(
        rejectError((error) => {
          this.errorAndExit(`Failed to fetch STM data to display`)
        }),
      )
      .subscribe((data) => {
        ;(this.store.dispatch(SubmitToMarketLocalActions.stmDataFetchSuccess({ data })),
          this.clearLoadingBlock(loadingBlockId))
      })
  }

  public getCodeMappingListData() {
    const loadingBlockId = this.createLoadingLock(LoadingTasks.FetchingSTMData)
    return this.sageLinkService
      .getCodeMappingListData()
      .pipe(
        rejectError((error) => {
          this.errorAndExit(`Failed to fetch code mapping list data`)
        }),
      )
      .subscribe((data) => {
        ;(this.store.dispatch(SubmitToMarketLocalActions.codeMappingListFetchSuccess({ data })),
          this.clearLoadingBlock(loadingBlockId))
      })
  }

  public getPreviousMarketsData(draftId: number, layerRefs: string) {
    const loadingBlockId = this.createLoadingLock(LoadingTasks.FetchingPreviousMarkets)
    return this.sageLinkService
      .getPreviousMarketsData(draftId, layerRefs)
      .pipe(
        rejectError((error) => {
          this.errorAndExit(`Failed to fetch previous markets data`)
        }),
      )
      .subscribe((data) => {
        ;(this.store.dispatch(SubmitToMarketLocalActions.previousMarketsFetchSuccess({ data })),
          this.clearLoadingBlock(loadingBlockId))
      })
  }

  public createEmailDrafts(draftId: number, emails: EmailDto[]) {
    const loadingBlockId = this.createLoadingLock(LoadingTasks.SendingEmails)
    const emailSent = this.submitToMarketApiService.sendEmails(draftId, emails).pipe(
      rejectError((error) => {
        this.errorAndExit(`Failed to send emails`)
      }),
      map((reinsurers) => ({ draftId, reinsurers })),
      tap((data) => {
        this.clearLoadingBlock(loadingBlockId)
        this.snackbar.openFromComponent(ErrorComponent, {
          data: errorPayload(`Created ${data.reinsurers.length}/${emails.length} draft emails`, {
            info: true,
          }),
          panelClass: 'app-success',
          duration: 3000,
        })
      }),
    )

    return emailSent
  }
  public createQuotePanels(draftId: number, reinsurerId: number[]) {
    const loadingBlockId = this.createLoadingLock(LoadingTasks.CreateQuotePanel)
    // [SAGE-DIAGNOSTICS AB#24735] interaction timer — token ends in the .then/.catch below; keep balanced, do not remove (breaks the metric).
    const token = this.interactions.start('RequestForQuote:CreateQuotePanels')
    this.submitToMarketApiService
      .createQuotePanels(draftId, reinsurerId)
      .pipe(
        rejectError((error) => {
          this.interactions.end(token, { success: false })
          this.errorAndExit(`Failed to create quote panels`)
        }),
      )
      .subscribe((data) => {
        this.interactions.end(token, { success: true, itemCount: reinsurerId.length })
        this.clearLoadingBlock(loadingBlockId)
        this.router.navigate(['submit-to-market'])
        this.snackbar.openFromComponent(ErrorComponent, {
          data: errorPayload(
            `Created ${data.riskSections.length} risk sections and ${data.riskReinsurers.length} risk reinsurers`,
            { info: true },
          ),
          panelClass: 'app-success',
          duration: 3000,
        })
      })
  }

  public updateDraftDashboard(draftId: number, dashboards: DraftDashboardListItem[]) {
    const loadingBlockId = this.createLoadingLock(LoadingTasks.UpdatingDashboard)
    return this.submitToMarketApiService.updateDraftDashboard(draftId, dashboards).pipe(
      rejectError((error) => {
        this.errorAndExit(`Failed to update draft dashboard`)
      }),
      tap(() => {
        this.clearLoadingBlock(loadingBlockId)
      }),
    )
  }

  public updateDraftMarketMapping(draftId: number, mapping: MarketLayerMapping[]) {
    const loadingBlockId = this.createLoadingLock(LoadingTasks.UpdatingMarkets)
    return this.submitToMarketApiService.updateDraftMarketMappping(draftId, mapping).pipe(
      rejectError((error) => {
        this.errorAndExit(`Failed to update draft market mapping`)
      }),
      tap(() => {
        this.clearLoadingBlock(loadingBlockId)
      }),
    )
  }

  public updateDraftTemplates(
    draftId: number,
    templates: EmailTemplate[],
    mapping: EmailTemplateMapping[],
  ) {
    const loadingBlockId = this.createLoadingLock(LoadingTasks.UpdateTemplates)
    return this.submitToMarketApiService
      .updateDraftEmailTemplates(draftId, templates, mapping)
      .pipe(
        rejectError((error) => {
          this.errorAndExit(`Failed to update draft template`)
        }),
        tap(() => {
          this.clearLoadingBlock(loadingBlockId)
        }),
      )
  }

  public getAllBrokers() {
    return this.submitToMarketApiService.getAllBrokers().pipe(
      rejectError((error) => {
        this.errorAndExit(`Failed to get brokers`)
      }),
    )
  }

  public createDraftTemplate(draftId: number) {
    return this.submitToMarketApiService.createDraftTemplate(draftId).pipe(
      rejectError((error) => {
        this.errorAndExit(`Failed to update draft attachment mapping`)
      }),
    )
  }

  public updateDraftAttachmentMapping(draftId: number, mapping: AttachmentMapping[]) {
    const loadingBlockId = this.createLoadingLock(LoadingTasks.UpdateAttachment)
    return this.submitToMarketApiService.updateDraftAttachmentMappping(draftId, mapping).pipe(
      rejectError((error) => {
        this.errorAndExit(`Failed to update draft attachment mapping`)
      }),
      tap(() => {
        this.clearLoadingBlock(loadingBlockId)
      }),
    )
  }

  public uploadAttachment(draftId: number, attachments: UploadAttachment[]) {
    const loadingBlockId = this.createLoadingLock(LoadingTasks.UploadingAttachments)
    return this.submitToMarketApiService.uploadDraftAttachments(draftId, attachments).pipe(
      rejectError((error) => {
        this.errorAndExit(`Failed to update draft structures`)
      }),
      tap(() => {
        this.clearLoadingBlock(loadingBlockId)
      }),
    )
  }

  public uploadCatModelingFilesAsAttachments(
    draftId: number,
    fileIdsToAttach: number[],
    fileIdsToDetach: number[],
  ) {
    const loadingBlockId = this.createLoadingLock(LoadingTasks.UploadingAttachments)
    return this.submitToMarketApiService
      .uploadCatModelingFilesAsAttachments(draftId, fileIdsToAttach, fileIdsToDetach)
      .pipe(
        rejectError((error) => {
          this.errorAndExit(`Failed to update cat modeling files as attachments`)
        }),
        tap(() => {
          this.clearLoadingBlock(loadingBlockId)
        }),
      )
  }

  public uploadCatModelingEntriesAsAttachments(
    draftId: number,
    entryIdsToAttach: number[],
    entryIdsToDetach: number[],
  ) {
    const loadingBlockId = this.createLoadingLock(LoadingTasks.UploadingAttachments)
    return this.submitToMarketApiService
      .uploadCatModelingEntriesAsAttachments(draftId, entryIdsToAttach, entryIdsToDetach)
      .pipe(
        rejectError((error) => {
          this.errorAndExit(`Failed to update cat modeling entries as attachments`)
        }),
        tap(() => {
          this.clearLoadingBlock(loadingBlockId)
        }),
      )
  }

  public updateDraftStructures(draftId: number, structures: number[]) {
    const loadingBlockId = this.createLoadingLock(LoadingTasks.UpdatingPortfolio)
    return this.submitToMarketApiService.updateDraftStructures(draftId, structures).pipe(
      rejectError((error) => {
        this.errorAndExit(`Failed to update draft structures`)
      }),
      tap(() => {
        this.clearLoadingBlock(loadingBlockId)
      }),
    )
  }

  public updateMarketOverrides(draftId: number, overrides: STMMarketOverride[]) {
    const loadingBlockId = this.createLoadingLock(LoadingTasks.UpdatingOverrides)
    return this.submitToMarketApiService.updateMappingOverrides(draftId, overrides).pipe(
      rejectError((error) => {
        this.errorAndExit(`Failed to update overrides`)
      }),
      tap(() => {
        this.clearLoadingBlock(loadingBlockId)
      }),
    )
  }

  public postAdeptSubmitToMarket(
    draftId: number,
    submissionDetailsList: STMSageLinkDisplayResponse[],
  ) {
    const loadingBlockId = this.createLoadingLock(LoadingTasks.SendingSageLinkRequest)
    const processID = this.sageLinkService
      .postAdeptSubmitToMarket(draftId, submissionDetailsList)
      .pipe(
        map((data) => {
          this.store.dispatch(fetchLatestPrograms())
          return data
        }),
        rejectError((error) => {
          this.errorAndExit(`Failed to submit to SageLink`)
        }),
        finalize(() => {
          this.clearLoadingBlock(loadingBlockId)
          this.snackbar.openFromComponent(ErrorComponent, {
            data: errorPayload(`Submitted to SageLink`, { info: true }),
            panelClass: 'app-success',
            duration: 3000,
          })
        }),
      )

    return processID
  }
}
